/**
 * "Take me back to where I was going" after sign-in.
 *
 * A signed-out visitor who follows a link (a push, an email) lands on the sign-in page.
 * The destination travels in `?returnTo=` and is used once they are signed in.
 * Only same-site paths are accepted, so a crafted link cannot send anyone elsewhere.
 */

export const RETURN_TO_PARAM = 'returnTo';

/** Longest destination we carry around. Keeps the sign-in link short. */
const MAX_LENGTH = 500;

/**
 * Returns the destination when it is a safe, same-site path ("/admin/new-members?x=1"),
 * otherwise null. Rejects other sites ("//evil.com", "https://evil.com"), backslash tricks,
 * control characters, and the sign-in page itself (which would loop).
 */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || raw.length > MAX_LENGTH) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (raw.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;

  // Resolve it against a fixed origin: anything that would leave that origin is refused.
  const base = 'https://app.invalid';
  let url: URL;
  try {
    url = new URL(raw, base);
  } catch {
    return null;
  }
  if (url.origin !== base) return null;

  // "/" and "/?returnTo=..." are where we send people to sign in; going back there loops.
  if (url.pathname === '/' && url.searchParams.has(RETURN_TO_PARAM)) return null;

  return url.pathname + url.search + url.hash;
}

/** The sign-in address that remembers where the visitor was heading. */
export function signInUrlFor(destination: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const target = safeReturnTo(
    `${destination.pathname}${destination.search ?? ''}${destination.hash ?? ''}`
  );
  return target ? `/?${RETURN_TO_PARAM}=${encodeURIComponent(target)}` : '/';
}
